import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmer } from '../components/Confirm';
import {
  api, type PurchasesPage, type PurchaseDetail, type PurchaseFilter,
  type InvoiceStateFilter, type PurchasePayFilter,
} from '../api';
import { BusinessDate } from '../components/BusinessDate';
import Pager from '../components/Pager';
import { useSort, SortTh, SearchInput, FilterSelect } from '../components/ListSort';
import { PickingPdfLink, ProductLink, stripCode } from '../components/Links';
import { useIsMobile, useScrollToDetail } from '../useIsMobile';
import { useChat } from '../ChatEngine';
import { ReceiptBadge, InvoiceBadge } from '../components/StatusBadge';

function fmtMoney(n: number, currency: string | null): string {
  return `${n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
}
function fmtDate(d: string | false): string {
  if (!d) return '—';
  const p = String(d).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(d);
}

type Toast = { kind: 'ok' | 'err'; text: string } | null;

const PAGE_SIZE = 25;

export default function Achats() {
  const [page, setPage] = useState<PurchasesPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Filtre et facture ouverte vivent dans l'URL : `/achats?facture=<id>` est la
  // cible des liens venus des mouvements de stock et de la traçabilité d'un lot.
  const [params, setParams] = useSearchParams();
  const filter: PurchaseFilter = params.get('filtre') === 'missing' ? 'missing' : 'all';
  const selectedId = Number(params.get('facture')) || null;
  function patch(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    setParams(p, { replace: true });
  }
  const setSelectedId = (id: number | null) => patch({ facture: id ? String(id) : null });
  useScrollToDetail(selectedId);
  const isMobile = useIsMobile();
  const chat = useChat();
  const [detail, setDetail] = useState<PurchaseDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const { sort, dir, toggle } = useSort();
  const [q, setQ] = useState('');
  const [etat, setEtat] = useState<InvoiceStateFilter>('tout');
  const [paiement, setPaiement] = useState<PurchasePayFilter>('tout');

  // Tri, recherche, filtres et page sont TOUS résolus par le serveur : l'état de
  // réception est calculé après lecture, filtrer côté navigateur ne verrait que la
  // page affichée. Recharger à chaque changement.
  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    return api.listPurchases({ filter, etat, paiement, sort, dir, q, limit: PAGE_SIZE, offset })
      .then(setPage).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [filter, etat, paiement, sort, dir, q, offset]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    api.getPurchase(selectedId).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }, [selectedId]);

  const items = page?.items ?? [];
  // Compteur calculé par le serveur sur TOUTES les factures, pas sur la page.
  const missingCount = page?.counts.missing ?? 0;

  // Changer de tri renvoie en page 1 : garder l'offset afficherait la page 3
  // d'un classement qui n'existe plus.
  const onSort = (c: string) => { toggle(c); setOffset(0); };

  function changeFilter(f: PurchaseFilter) {
    patch({ filtre: f === 'missing' ? 'missing' : null, facture: null });
    setOffset(0);
  }

  async function askAssistant() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name}`).join('\n');
    const prefix =
      `Je regarde la facture fournisseur Odoo **${detail.name}** (account.move id ${detail.id}, réf ${detail.ref || '—'}).\n` +
      `- Fournisseur : ${detail.partner ?? '—'} · Total : ${fmtMoney(detail.amount_total, detail.currency)}\n` +
      `- Réception : ${detail.receptions.map((r) => `${r.name} (${r.state})`).join(', ') || 'aucune'}\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    try {
      // Contexte toujours pré-rempli, y compris sur une conversation déjà ouverte
      // pour cette facture (cf. `useAskAssistant` dans Ventes.tsx).
      const { conversation } = await api.openDocConversation(`account.move:${detail.id}`, `Facture fourn. ${detail.name}`);
      chat.openDock({ conv: conversation.id, draft: prefix });
    } catch (e: any) { setToast({ kind: 'err', text: e.message }); }
  }

  async function validerFacture() {
    if (!detail) return;
    if (!await confirmer(`Comptabiliser la facture ${detail.name} ?\n\nElle deviendra définitive et numérotée dans la comptabilité.`)) return;
    setPosting(true);
    try {
      const r = await api.postPurchase(detail.id);
      setToast({ kind: 'ok', text: `Facture ${r.name ?? detail.name} comptabilisée.` });
      await reload();
      setDetail(await api.getPurchase(detail.id));
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message ?? 'Erreur' });
    } finally {
      setPosting(false);
    }
  }

  const detailPanel = selectedId == null ? null : (
    <aside className="devis-detail">
      <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
      {detailLoading && <div className="devis-empty">Chargement…</div>}
      {detail && (
        <>
          <h2>{detail.name}{detail.move_type === 'in_refund' && <span className="devis-kind"> avoir</span>}</h2>
          <div className="devis-meta">
            <div><span>Fournisseur</span><strong>{detail.partner ?? '—'}</strong></div>
            {detail.ref && <div><span>Réf. fournisseur</span><strong>{detail.ref}</strong></div>}
            <div><span>État</span><strong><InvoiceBadge state={detail.state} payment={detail.payment_state} /></strong></div>
            <div><span>Réception</span><strong><ReceiptBadge receipt={detail.receipt} /></strong></div>
            <div><span>Date</span><strong><BusinessDate value={detail.invoice_date} createdAt={detail.created_at} /></strong></div>
          </div>

          <table className="devis-lines">
            <thead><tr><th>Article</th><th className="num">Qté</th><th className="num">P.U.</th><th className="num">Sous-total</th></tr></thead>
            <tbody>
              {detail.lines.map((l) => (
                <tr key={l.id}>
                  <td><ProductLink id={l.product_id}>{stripCode(l.product) || l.name}</ProductLink></td>
                  <td className="num">{l.qty}</td>
                  <td className="num">{fmtMoney(l.price_unit, null)}</td>
                  <td className="num">{fmtMoney(l.subtotal, null)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="devis-totals">
            <div><span>Hors taxe</span><strong>{fmtMoney(detail.amount_untaxed, detail.currency)}</strong></div>
            <div><span>Taxes</span><strong>{fmtMoney(detail.amount_tax, detail.currency)}</strong></div>
            <div className="total"><span>Total</span><strong>{fmtMoney(detail.amount_total, detail.currency)}</strong></div>
          </div>

          <div className="devis-meta" style={{ marginTop: 8 }}>
            <div>
              <span>Réceptions liées</span>
              <strong>
                {detail.receptions.length
                  ? detail.receptions.map((r, i) => (
                    <span key={r.id}>
                      {i > 0 && ', '}
                      <PickingPdfLink id={r.id} name={r.name} /> ({r.state})
                    </span>
                  ))
                  : '—'}
              </strong>
            </div>
          </div>

          {detail.receipt === 'missing' && (
            <div className="devis-warn">⚠️ Marchandise non reçue : cette facture contient des produits stockables mais aucune réception validée — le stock n'a pas été incrémenté.</div>
          )}
          {detail.receipt === 'service' && (
            <div className="devis-meta"><div><span></span><strong style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>Facture de service/frais : aucune réception attendue.</strong></div></div>
          )}

          <div className="devis-actions">
            {detail.state === 'draft' && (
              <button className="devis-validate" onClick={validerFacture} disabled={posting}>
                {posting ? 'Comptabilisation…' : '✓ Valider la facture'}
              </button>
            )}
            <a className="devis-odoo" href={`/api/purchases/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
            <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
            <button className="devis-ask" onClick={askAssistant}>💬 Demander une modification</button>
          </div>
        </>
      )}
    </aside>
  );

  return (
    <div className="devis-page">
      <div className="page-toolbar">
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>

      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}

      <div className="ventes-toolbar">
        <div className="suivi-filters">
          <button className={`suivi-filter ${filter === 'all' ? 'active' : ''}`} onClick={() => changeFilter('all')}>
            Toutes{page ? ` (${page.counts.total})` : ''}
          </button>
          <button className={`suivi-filter ${filter === 'missing' ? 'active' : ''}`} onClick={() => changeFilter('missing')}>
            Marchandise non reçue{missingCount ? ` (${missingCount})` : ''}
          </button>
        </div>
        <SearchInput value={q} onChange={(v) => { setQ(v); setOffset(0); }}
          placeholder="N°, réf. de facture ou fournisseur…" />
        <FilterSelect label="État" value={etat}
          onChange={(v) => { setEtat(v); setOffset(0); }}
          options={[['tout', 'Tous'], ['draft', 'Brouillon'], ['posted', 'Validée']]} />
        <FilterSelect label="Paiement" value={paiement}
          onChange={(v) => { setPaiement(v); setOffset(0); }}
          options={[['tout', 'Tous'], ['unpaid', 'Non payée'], ['paid', 'Payée']]} />
      </div>

      <div className="devis-body">
        <div className="devis-list">
          {/* Facture ouverte par lien direct (`?facture=`) : elle n'est pas forcément
              dans la page listée — sur mobile, où le détail se déplie sous sa ligne,
              il faut alors l'afficher au-dessus de la liste. */}
          {isMobile && selectedId != null && !items.some((m) => m.id === selectedId) && detailPanel}
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">Aucune facture fournisseur.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead>
                <tr>
                  <SortTh col="numero" label="N° / Réf." sort={sort} dir={dir} onSort={onSort} />
                  <SortTh col="client" label="Fournisseur" sort={sort} dir={dir} onSort={onSort} />
                  <SortTh col="montant" label="Montant" className="num" sort={sort} dir={dir} onSort={onSort} />
                  <SortTh col="date" label="Date" sort={sort} dir={dir} onSort={onSort} />
                  <SortTh col="etat" label="État" sort={sort} dir={dir} onSort={onSort} />
                  <SortTh col="reception" label="Réception" sort={sort} dir={dir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <Fragment key={m.id}>
                  <tr className={m.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(m.id)}>
                    <td className="mono cell-title">{m.name}{m.move_type === 'in_refund' && <span className="devis-kind"> avoir</span>}{m.ref && m.name !== m.ref ? <span className="ref-sub"> · {m.ref}</span> : ''}</td>
                    <td data-label="Fournisseur">{m.partner ?? '—'}</td>
                    <td className="num" data-label="Montant">{fmtMoney(m.amount_total, m.currency)}</td>
                    <td data-label="Date"><BusinessDate value={m.invoice_date} createdAt={m.created_at} /></td>
                    <td data-label="État"><InvoiceBadge state={m.state} payment={m.payment_state} /></td>
                    <td data-label="Réception"><ReceiptBadge receipt={m.receipt} /></td>
                  </tr>
                  {/* Sur mobile il n'y a pas de colonne de droite : le détail
                      s'ouvre sous la carte touchée, pas en bas de la page. */}
                  {isMobile && m.id === selectedId && (
                    <tr className="detail-row detail-row-panel">
                      <td colSpan={6}>{detailPanel}</td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {!loading && !error && page && (
            <Pager
              total={page.total} limit={page.limit} offset={page.offset}
              onOffset={(o) => { setOffset(o); setSelectedId(null); }}
              unit={['facture', 'factures']}
              capped={page.counts.capped}
              cappedNote="au-delà de 1000 factures fournisseur, les plus anciennes ne sont pas lues"
            />
          )}
        </div>

        {!isMobile && detailPanel}
      </div>
    </div>
  );
}
